/**
 * Role capability catalog and the seeded permission-set templates (NEX-2514
 * capability slice).
 *
 * THE MODEL, DECIDED. One vocabulary — the capabilities below — over two SCOPES
 * of grant, unioned additively, with absence granting nothing:
 *
 * - **Role scope.** `RoleGroupCapability` via `RoleGroupMember`: "may you do
 *   this ONE thing inside THIS Role". Every capability except the org-scoped-only
 *   ones can be held here.
 * - **Org scope.** `RoleCapabilityGrant`, keyed on the organisation with no
 *   `roleId`: "may you do this thing anywhere in this organisation, including
 *   where no Role exists yet". `ORG_SCOPED_ONLY_CAPABILITIES` is the set that can
 *   ONLY be held here, because it names no Role — `role.create` is the first.
 *
 * `ResolveRoleCapabilityHoldingUseCase` reads both and returns their UNION, so a
 * caller holds a capability if either scope grants it. Neither scope has a deny
 * row and no consumer reads the ABSENCE of a capability as authority; that
 * monotonicity is what makes the union safe, and it is measured rather than
 * assumed — check it again before adding a third consumer.
 *
 * An action still either RESOLVES to one `roleId` or it does not, and that
 * decides which SCOPE answers rather than which system:
 *
 * - NO `roleId` — "create a Role", the detach of a resource that is in no Role.
 *   `RoleCapabilityGuard` passes `roleId: null` and the ORG scope answers.
 * - ONE `roleId` — everything done to ONE existing Role. Both scopes answer, and
 *   the union is what the caller holds.
 *
 * "Resolves", not "carries in the path". Most Role-scoped routes name the Role
 * as `/api/roles/:id`, but detach does not: `DELETE
 * /api/roles/resources/:resourceType/:resourceId` names only the resource, and
 * the Role is recovered from the `RoleResource` row —
 * `@@unique([organizationId, resourceType, resourceId])` guarantees at most
 * one, and `UnassignResourceUseCase` already reads it back as
 * `removedFromRoleId`. So `resource.detach` is normally Role-scoped like every
 * other capability here, and the guard resolves that Role FIRST; on an UNATTACHED
 * resource it resolves none, and the org scope answers instead.
 *
 * 🚨 THERE WAS A SECOND AUTHORIZATION AXIS AND IT IS GONE. `RoleManagementAction`
 * named an ORG-WIDE allow-list over `RoleManagementGrant`, enforced by
 * `RoleManagementActionGuard` in front of the capability guard on every mutating
 * Roles route and by an explicit call on every v1 twin. Two vocabularies over one
 * request meant two questions asked of it, answered by the stricter of two
 * decisions that were never designed to agree — and the stricter one was
 * UNANSWERABLE: `RoleManagementGrant` had exactly one writer, an org-admin
 * settings panel, nothing seeded it, and its documented no-rows policy was "org
 * admins only". So on every dual-gated route a caller who was neither an org
 * admin nor the Role's owner was refused BEFORE the capability layer ran, and
 * every permission set an administrator had granted through a Permission set was
 * inert while the permission UI rendered it as held. Measured on production: 0
 * grant rows across 45 organisations, so the defect was total rather than partial.
 *
 * The axis was shed route by route and then deleted — guard, decorators,
 * entity, ports, adapters and allow-list reads alike. What replaced it is the org
 * SCOPE above: an org-wide grant of the same standing, in the vocabulary the rest
 * of this surface already speaks. `RoleManagementPolicy` and the request queue
 * are untouched and are not part of that axis — they answer "does this write file
 * a request for review instead of acting", which is a third outcome no guard can
 * express and no permission decides.
 *
 * ⚠️ THE TS `RoleManagementAction` KEEPS ALL FIVE VALUES AND THE POSTGRES ENUM
 * HOLDS TWO — that gap is deliberate and is not drift. The database stores only
 * `CREATE_ROLE` and `DELETE_ROLE`, the two with an approval queue and the only
 * ones ever written (see `APPROVAL_QUEUED_ROLE_MANAGEMENT_ACTIONS`); the wider
 * TS union exists so a caller naming a retired value is refused with a reason
 * rather than a 404. Read it as the wire vocabulary, never as a mirror of the
 * column — `role-membership.types.ts` owns why, including the asymmetry that a
 * label is cheap to add to the Postgres type and a full table rewrite under
 * `AccessExclusiveLock` to remove.
 *
 * CAPABILITIES ARE NOT RESOURCE ACCESS. Nothing here writes a
 * `ResourcePermission` row. Reaching a Role's agents/workflows/deployments is
 * governed by the group's `resourceRelation` (a per-caller CAP applied at
 * subject-resolution time), and reaching an app area by its surfaces
 * (`AppFeatureKey`, a separate axis — see `features.constants.ts`). A group may
 * legitimately hold capabilities and NO `resourceRelation` at all; that state is
 * representable here by design (`resourceRelation: null`).
 *
 * Frontend-safe: zero Prisma runtime dependency. A const union rather than a
 * DB enum, so the catalog can grow without a migration — the same choice
 * `RoleGroupGrant.surface` already makes for `AppFeatureKey`.
 *
 * Each `export const X` is paired with an `export type X` of the same name —
 * the const is the runtime source of truth, the type its compile-time
 * projection. TypeScript merges the two by design (`no-redeclare` is off for
 * this package precisely so that idiom compiles clean).
 */
import { z } from "zod";

import { Relation } from "../permissions/permissions.types";

/**
 * The closed set of things a subject can do to ONE existing Role.
 *
 * Lowercase dotted `<area>.<verb>`, one area per tab of the Role detail
 * surface. Derived from the Role-scoped HTTP surface (every route under
 * `/api/roles/:id`), so each value names an action that already exists rather
 * than one someone might want later.
 *
 * A `.view` capability gates a TAB; `role.view` gates the detail page itself.
 * The read side is currently ungated org-wide, so the seeded templates below
 * both hold every `.view` — withholding one is what a CUSTOM group is for
 * (an auditors group that sees the team but not the knowledge grants), never
 * something the migration does to an existing member.
 *
 * `resource.attach` and `resource.detach` split what the retired
 * `MANAGE_RESOURCES` action fused, because detaching leaves a system unattached while
 * attaching cannot. Both stay Maintainer-held; the split exists so a custom
 * group can hold one without the other.
 *
 * ── OWNERSHIP TRANSFER IS NOT A CAPABILITY, AND MUST NOT BECOME ONE ─────────
 *
 * 🚨 There is deliberately no `role.transfer_ownership` here. Handing
 * `Role.ownerUserId` to somebody else is authorised by IDENTITY, not by a
 * grant: `UpdateRoleUseCase` reserves it to the Role's CURRENT OWNER and to
 * organisation admins, and `update-role.use-case.spec.ts` pins both arms. A
 * permission set cannot satisfy an identity check, so a capability for it would
 * be unsatisfiable by construction — and it WAS here, ticked in the permission
 * UI, evaluated by no route on either API, granting nothing to whoever held it.
 *
 * Adding it back would not wire that gate up; it would REPLACE an owner-only
 * rule with a box any admin can tick for any group, which is a weakening
 * dressed as a fix. If transfer ever needs delegating, the decision to make is
 * about `UpdateRoleUseCase`'s predicate — not about this list.
 */
export const RoleCapability = {
  /**
   * Bring a new Role into existence.
   *
   * 🚨 THE ONE CAPABILITY THAT NAMES NO ROLE, AND THE ONLY REASON IT BELONGS IN
   * THIS CATALOG IS THAT THE CATALOG NOW HAS AN ORG SCOPE. Every other value
   * here is answered inside ONE existing Role; there is no Role to answer this
   * one in, so it is held through `RoleCapabilityGrant` — the org-scoped table —
   * and never through a `RoleGroupCapability` row, which is keyed to a Role that
   * by definition does not exist yet.
   *
   * That is not a stylistic split. It is enforced in three places, because a
   * Role-scoped grant of it would be unsatisfiable and would still render as
   * held: {@link ORG_SCOPED_ONLY_CAPABILITIES} keeps it out of both seeded
   * templates, `assertPermissionSetCapabilities` refuses it at the permission-set
   * write boundary, and the guard resolves it against the org scope alone.
   */
  ROLE_CREATE: "role.create",

  /** Open the Role's detail page at all. */
  ROLE_VIEW: "role.view",
  /** Rename the Role, rewrite its job description. */
  ROLE_UPDATE: "role.update",
  /** Destroy the Role. Its resources are unassigned, never deleted. */
  ROLE_DELETE: "role.delete",

  /**
   * Stop the Role's work: its paused systems refuse to execute.
   *
   * 🚨 IT DOES NOT TOUCH ACCESS, AND THE OTHER READING WAS REFUSED FOR A
   * REASON THAT IS WORTH KEEPING WRITTEN DOWN. "Suspend what the Role grants"
   * sounds like the safer of the two and does not withdraw access at all: a Role
   * is the LAST HOLDER of the resources it exclusively owns, and the console
   * permission family is an ALLOW-LIST over the caller's own placement, so
   * emptying its grants returns those resources to the set no Role has claimed,
   * where every caller placed in no Role reaches them. Pausing is about
   * execution and
   * nothing else — `ExecutionGuardPort` is where it lands, `ResourcePermission`
   * is untouched, and a member of a paused Role reaches exactly what they
   * reached before.
   *
   * ITS OWN CAPABILITY, PAIRED WITH `role.resume` RATHER THAN FUSED WITH IT,
   * for the reason `resource.attach` / `resource.detach` are two: the pair is
   * ASYMMETRIC. Pausing stops customer-facing work and resuming restarts it, so
   * an on-call group that may restart a Role somebody else stopped — and may not
   * stop one itself — is a real audience, and a single `role.pause_resume`
   * cannot express it.
   */
  ROLE_PAUSE: "role.pause",
  /** Start the Role's work again, clearing the stop its systems are held by. */
  ROLE_RESUME: "role.resume",

  /** See who is in the Role and which group each person sits in. */
  TEAM_VIEW: "team.view",
  /** Add and remove people, move them between groups. */
  TEAM_MANAGE: "team.manage",

  /** See the Role's permission sets, their capabilities and their surfaces. */
  GROUP_VIEW: "group.view",
  /** Create, edit and delete custom groups; edit a group's surfaces. */
  GROUP_MANAGE: "group.manage",

  /** See the systems (agents, workflows, deployments, templates) in the Role. */
  RESOURCE_VIEW: "resource.view",
  /** Put an unattached system into the Role. */
  RESOURCE_ATTACH: "resource.attach",
  /**
   * Take a system out of the Role, returning it to the unattached resource pool.
   *
   * The Role is resolved from the `RoleResource` row rather than read off the
   * path — see the invariant in this file's header before wiring the guard.
   */
  RESOURCE_DETACH: "resource.detach",

  /** See which knowledge collections the Role reaches. */
  COLLECTION_GRANT_VIEW: "collection_grant.view",
  /** Grant and revoke the Role's knowledge collections. */
  COLLECTION_GRANT_MANAGE: "collection_grant.manage",

  /** See which file workspaces the Role reaches. */
  WORKSPACE_GRANT_VIEW: "workspace_grant.view",
  /** Grant and revoke the Role's file workspaces. */
  WORKSPACE_GRANT_MANAGE: "workspace_grant.manage",

  /** See which external tools (plugins) the Role reaches. */
  EXTERNAL_TOOL_GRANT_VIEW: "external_tool_grant.view",
  /**
   * Grant and revoke the Role's external tools.
   *
   * ⚠️ ITS OWN PAIR RATHER THAN A REUSE OF `resource.attach` / `resource.detach`,
   * and the reason is structural rather than tidiness: those two govern
   * `RoleResource`, where attachment is EXCLUSIVE and detaching therefore
   * UNATTACHED_RESOURCES a system. A tool grant is M:N — revoking this Role's tool leaves
   * every other Role that holds it untouched and unattached resources nothing — so the claim
   * being authorised is a different one, and the pair alongside
   * `collection_grant.*` and `workspace_grant.*` is where it belongs.
   *
   * 🚨 BOTH ARE NEW CAPABILITY STRINGS, SO NO EXISTING `RoleGroupCapability` ROW
   * HOLDS EITHER. The two seeded templates below are DERIVED — `.view` lands in
   * Member automatically and `.manage` in Maintainer — but those templates are a
   * SEED taken at group-creation time and are never re-applied, exactly as
   * `FeatureFlag.enabled` is. So a subject resolved through a permission set
   * that already exists is refused until a backfill inserts the rows; an
   * organisation admin and a Role owner are unaffected, because both
   * short-circuit before any grant is read.
   */
  EXTERNAL_TOOL_GRANT_MANAGE: "external_tool_grant.manage",

  /**
   * See the Role's automation-coverage model and the percentage it produces.
   *
   * A read, so it follows the `.view` rule below and Member holds it: the
   * figure is the Role's own headline number, and a member who can open the
   * Role can see what it is.
   */
  COVERAGE_VIEW: "coverage.view",
  /**
   * Author the Role's workload model, its systems' impact models, and the
   * working-time assumptions those rest on.
   *
   * ITS OWN capability, deliberately separate from `resource.attach` — putting
   * a system into a Role and stating how much work that system removes are
   * different claims, made by different people, and the second is the one that
   * reaches a customer-facing ROI figure. Holding it confers no resource
   * access; see the CAPABILITIES ARE NOT RESOURCE ACCESS note above.
   */
  COVERAGE_MANAGE: "coverage.manage",

  /**
   * See the Role's Overview layout — its boards, and which card sits on which.
   *
   * A read, so Member holds it by the `.view` rule: the lanes ARE how the
   * Overview screen renders a Role, and a member who can open the Role sees the
   * arrangement of it.
   *
   * ⚠️ IT SPANS ALL FOUR CARD KINDS, WHICH IS WIDER THAN ANY ONE TAB. A lane
   * holds a mixture of systems, Collection grants, Workspace grants and
   * external Tool grants, so the placement read necessarily discloses which
   * Collections, Workspaces and Tools the Role holds — the same facts
   * `collection_grant.view`, `workspace_grant.view` and
   * `external_tool_grant.view` gate on their own tabs. Both seeded templates
   * hold all four, so the disclosure is invisible there; it is only reachable by
   * a CUSTOM group granting `board.view` while withholding one of the others.
   * That combination is representable and it is NOT what it looks like. Say so
   * to anyone authoring one.
   */
  BOARD_VIEW: "board.view",
  /**
   * Create, rename, recolour, delete and reorder the Role's boards, and drag a
   * card from one lane to another.
   *
   * ITS OWN capability rather than a reuse of `role.update` or
   * `resource.attach`, because those two name different claims: renaming a
   * LANE is not renaming the Role, and moving a card between lanes changes
   * nothing about what the Role contains. A board carries no permission meaning
   * and no execution meaning, so holding this confers no resource access and
   * removes none — see the CAPABILITIES ARE NOT RESOURCE ACCESS note above.
   *
   * Not a `.view`, so Member does not hold it: rearranging a shared board is an
   * act on everyone else's view of the Role, not on your own.
   */
  BOARD_MANAGE: "board.manage",

  /** See the Role's resource-access request queue. */
  ACCESS_REQUEST_VIEW: "access_request.view",
  /** File a request for access to one of the Role's resources. */
  ACCESS_REQUEST_CREATE: "access_request.create",
  /** Approve or reject a pending request. */
  ACCESS_REQUEST_REVIEW: "access_request.review"
} as const;

export type RoleCapability = (typeof RoleCapability)[keyof typeof RoleCapability];

export const ROLE_CAPABILITIES = Object.values(RoleCapability) as [
  RoleCapability,
  ...RoleCapability[]
];

export const RoleCapabilitySchema = z.enum(ROLE_CAPABILITIES);

/**
 * Runtime membership check for a `RoleGroupCapability.capability` value.
 *
 * A type guard rather than a cast, and the exact counterpart of
 * {@link isPermissionSetSurfaceValue}. The column is TEXT and its CHECK constrains
 * the SHAPE only (`^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$`) — deliberately, so the
 * catalog can grow without a migration. `spurious.verb` therefore reaches the
 * database happily. Membership is the read and write paths' job, and skipping
 * it lands the codebase in "TEXT with no validation anywhere".
 *
 * READS drop what they cannot recognise; WRITES refuse it. That asymmetry is
 * deliberate: a row written under a capability this build has since removed
 * must not grant anything, and a caller asking to store one must be told rather
 * than silently obeyed.
 */
export const isRoleCapability = (value: unknown): value is RoleCapability =>
  typeof value === "string" && (ROLE_CAPABILITIES as readonly string[]).includes(value);

/**
 * The two `isSystem` groups seeded into every Role. `key` is stable and
 * code-owned; the capability sets below are immutable in the product — only
 * MEMBERSHIP of these groups is editable. Custom groups are the identical
 * mechanism with `isSystem = false`.
 */
export const PermissionSetTemplateKey = {
  MAINTAINER: "maintainer",
  MEMBER: "member"
} as const;

export type PermissionSetTemplateKey =
  (typeof PermissionSetTemplateKey)[keyof typeof PermissionSetTemplateKey];

export const PERMISSION_SET_TEMPLATE_KEYS = Object.values(PermissionSetTemplateKey) as [
  PermissionSetTemplateKey,
  ...PermissionSetTemplateKey[]
];

export const PermissionSetTemplateKeySchema = z.enum(PERMISSION_SET_TEMPLATE_KEYS);

/** The seeded shape of one system group, before any membership exists. */
export interface PermissionSetTemplate {
  readonly key: PermissionSetTemplateKey;
  readonly name: string;
  readonly capabilities: readonly RoleCapability[];
  /**
   * The ceiling this group's members hit on the Role's resources, applied as a
   * per-caller CAP at subject-resolution time. `null` means the group confers
   * no resource access whatsoever — a capability-only group.
   */
  readonly resourceRelation: Relation | null;
  readonly position: number;
}

/**
 * The only capability Maintainer does NOT hold.
 *
 * "Maintainer can do everything except destructive" is expressed as exactly
 * this one absence. There is no tier branch anywhere and none is to be
 * introduced — adding one puts the codebase back where `RoleMemberTier` left
 * it, with two mechanisms answering the same question.
 *
 * Ownership transfer is the other thing a Maintainer cannot do, and it is
 * absent from this list because it is absent from the catalog: it is an
 * identity check in `UpdateRoleUseCase`, never a grant. See the note on
 * `RoleCapability` above before reaching for a second entry here.
 */
export const MAINTAINER_EXCLUDED_CAPABILITIES: readonly RoleCapability[] = [
  RoleCapability.ROLE_DELETE
];

/**
 * Capabilities that are held at the ORGANISATION scope and nowhere else.
 *
 * 🚨 THIS LIST EXISTS BECAUSE BOTH TEMPLATES ARE COMPUTED, AND A COMPUTED
 * DEFAULT IS A GRANT NOBODY WROTE. `MAINTAINER_CAPABILITIES` filters
 * `ROLE_CAPABILITIES`, so a value added to the catalog joins Maintainer by
 * construction — which is the correct default for every Role-scoped value and
 * is exactly wrong for one that names no Role. Adding `role.create` without
 * this filter would hand every Maintainer of every Role the power to create
 * Roles, through a derivation rather than a decision, and the permission UI
 * would render it as a deliberate grant.
 *
 * The exclusion is not merely cosmetic either: a `RoleGroupCapability` row is
 * keyed to ONE Role, and the guard resolves `role.create` with no Role at all,
 * so such a row could never be read. It would tick in the UI and grant nothing
 * — the dead-capability failure `every-role-capability-is-enforced.spec.ts`
 * exists to refuse, one grant table lower.
 *
 * SEPARATE FROM `MAINTAINER_EXCLUDED_CAPABILITIES`, which answers a different
 * question. That list is a TIER statement — "Maintainer can do everything
 * except destroy" — and Member's derivation ignores it, correctly, because
 * `role.delete` is a mutation Member never held anyway. This list is a SCOPE
 * statement and binds both templates, because neither of them is org-scoped.
 */
export const ORG_SCOPED_ONLY_CAPABILITIES: readonly RoleCapability[] = [RoleCapability.ROLE_CREATE];

/** True for a capability that only ever resolves against the organisation. */
export const isOrgScopedOnlyRoleCapability = (capability: RoleCapability): boolean =>
  ORG_SCOPED_ONLY_CAPABILITIES.includes(capability);

/**
 * Computed, never hand-listed: a capability added to the catalog joins
 * Maintainer automatically, so the set cannot silently fall behind.
 *
 * 🚨 THAT DEFAULT IS NOW LOAD-BEARING, BECAUSE THE GUARD REFUSES. A new
 * capability joining Maintainer automatically is the SAFE direction — the
 * alternative is a Maintainer who silently loses an action the day it is
 * added. A capability deliberately withheld belongs in
 * `MAINTAINER_EXCLUDED_CAPABILITIES`, where the test can see it and a reviewer
 * has to agree to it.
 */
const MAINTAINER_CAPABILITIES: readonly RoleCapability[] = ROLE_CAPABILITIES.filter(
  (capability) =>
    !MAINTAINER_EXCLUDED_CAPABILITIES.includes(capability) &&
    !isOrgScopedOnlyRoleCapability(capability)
);

/** The verb that marks a capability as a pure read. */
const VIEW_VERB = "view";

/** True for `<area>.view`, the read capability that gates one tab. */
export const isReadOnlyRoleCapability = (capability: RoleCapability): boolean =>
  capability.endsWith(`.${VIEW_VERB}`);

/**
 * The ONLY non-read capability a plain member holds, and it is one line so
 * that adding a second is a decision somebody writes down.
 *
 * `access_request.create` is a write that exists to ask permission, so
 * withholding it from members would leave the queue with nobody able to file
 * into it.
 */
export const MEMBER_NON_VIEW_CAPABILITIES: readonly RoleCapability[] = [
  RoleCapability.ACCESS_REQUEST_CREATE
];

/**
 * What a plain member of a Role keeps: every read, plus filing an access
 * request.
 *
 * COMPUTED, never hand-listed, for the same reason Maintainer's set is: a
 * capability added to the catalog must land on the correct side by CONSTRUCTION
 * rather than because somebody remembered. This list used to be eight literals,
 * and the gap that shape leaves is silent in the direction that matters — a new
 * MUTATING capability spelled into the catalog and forgotten here would simply
 * not be granted, which is safe, but a new READ capability forgotten here
 * withholds a tab from every member of every Role, with nothing failing. The
 * derivation below fixes both directions at once.
 *
 * The rule it encodes is the one this file already stated in prose: a `.view`
 * capability gates a tab, both seeded templates hold every `.view`, and
 * withholding one from a particular audience is what a CUSTOM group is for.
 *
 * ── WHY MEMBER HOLDS NO MUTATION ────────────────────────────────────────────
 *
 * Renaming a Role is `role.update`, a mutation, so Member does not hold it —
 * and that now follows from the derivation rather than from a list.
 *
 * 🚨 NEVER READ A GATE OFF AN ENDPOINT'S CURRENT POSTURE. "Whatever a member can
 * reach today is a member capability" is the reasoning that has to be refused
 * here, because it is circular: it takes the absence of a gate as evidence of
 * intent, and an absent gate is a defect that reads exactly like a decision.
 * `PATCH /api/roles/:id` was that defect — it shipped with its feature flag and
 * nothing else, and `role-routes-declare-governance.spec.ts` exists because of
 * it. Argue from what the action MEANS, then check the route enforces it.
 *
 * Everything else is strict identity with the eight capabilities Member held
 * before this became computed, and a test pins that identity element by element.
 */
const MEMBER_CAPABILITIES: readonly RoleCapability[] = ROLE_CAPABILITIES.filter(
  (capability) =>
    !isOrgScopedOnlyRoleCapability(capability) &&
    (isReadOnlyRoleCapability(capability) || MEMBER_NON_VIEW_CAPABILITIES.includes(capability))
);

/**
 * Both templates sit at `Relation.EDITOR`, and Member's is the load-bearing
 * one.
 *
 * `RESOURCE ACCESS` is tier-independent: `FindSubjectMembershipsPrismaAdapter`
 * builds its `roleIds` from the owner or ANY `user` seat with no tier
 * predicate, and `RolePermissionSyncService` writes one `SubjectType.ROLE` row
 * at EDITOR per (resource, Role) — never per member. So anything below
 * `editor` on the Member template silently downgrades every existing member
 * the moment the backfill runs. Moving Member to `viewer` is a deliberate
 * product change to make AFTER migration, per org, by editing the seeded
 * group.
 *
 * `CAPABILITY` is NOT tier-independent — see `templateKeyForMemberTier`
 * (`apps/backend/src/roles/domain/value-objects/role-permission-set-plan.ts`).
 * `RoleMemberTier.ADMIN` seats into `maintainer`, so an ADMIN and a MEMBER of
 * the same Role hold different capability sets: everything below except
 * `role.delete` and the org-scoped-only `role.create`.
 */
export const PERMISSION_SET_TEMPLATES: readonly PermissionSetTemplate[] = [
  {
    key: PermissionSetTemplateKey.MAINTAINER,
    name: "Maintainers",
    capabilities: MAINTAINER_CAPABILITIES,
    resourceRelation: Relation.EDITOR,
    position: 0
  },
  {
    key: PermissionSetTemplateKey.MEMBER,
    name: "Members",
    capabilities: MEMBER_CAPABILITIES,
    resourceRelation: Relation.EDITOR,
    position: 1
  }
];

/** Look one template up by key. Returns `undefined` for a custom group's key. */
export const findPermissionSetTemplate = (
  key: PermissionSetTemplateKey
): PermissionSetTemplate | undefined =>
  PERMISSION_SET_TEMPLATES.find((template) => template.key === key);
